import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Pill, TableCell, TableRow } from '@/shared/components';
import {
  propertyStatusOptions,
  type ObjectApartmentSummary,
  type PropertyLifecycleStatus,
} from '@/shared/api';
import type { NestedApartment } from '@/features/properties/flat-family';
import { cn } from '@/shared/lib/cn';

const statusLabel = (value: string): string =>
  propertyStatusOptions.find((option) => option.value === value)?.label ?? value;

const LIFECYCLE_VARIANT: Record<
  PropertyLifecycleStatus,
  'live' | 'warning' | 'default' | 'outline'
> = {
  Active: 'live',
  Draft: 'default',
  Paused: 'warning',
  Archived: 'outline',
};

export const apartmentLabel = (apartment: ObjectApartmentSummary) =>
  apartment.adminDisplayName ?? apartment.name;

export interface ApartmentRowProps {
  row: NestedApartment;
  /** Zahl der Zimmer, wenn das Apartment Gesamtwohnung ist. */
  roomCount: number;
  open: boolean;
  onToggle: () => void;
  capacity: string;
}

/** Eine Tabellenzeile; Zimmer stehen eingerückt unter ihrer Gesamtwohnung. */
export function ApartmentTableRow({ row, roomCount, open, onToggle, capacity }: ApartmentRowProps) {
  const { apartment, isRoom } = row;
  return (
    <TableRow className={cn(isRoom && 'bg-card-alt/40')}>
      <TableCell className={cn(isRoom && 'pl-12')}>
        <span className="flex min-w-0 items-center gap-2">
          {roomCount > 0 && (
            <FamilyToggle open={open} onToggle={onToggle} name={apartmentLabel(apartment)} />
          )}
          <span className="min-w-0">
            <Link
              to="/properties/$id"
              params={{ id: apartment.id }}
              className="focus-visible:ring-sage text-ink break-words rounded-sm font-medium hover:underline focus-visible:outline-none focus-visible:ring-2"
            >
              {apartmentLabel(apartment)}
            </Link>
            {familyLine(isRoom, roomCount) && (
              <span className="text-ink-soft block text-xs">{familyLine(isRoom, roomCount)}</span>
            )}
          </span>
        </span>
      </TableCell>
      <TableCell>
        <StatusPill status={apartment.status} />
      </TableCell>
      <TableCell className="text-ink-soft text-sm">{capacity}</TableCell>
    </TableRow>
  );
}

/** Dieselbe Zeile als Listeneintrag unter der Containerbreite. */
export function ApartmentListItem({ row, roomCount, open, onToggle, capacity }: ApartmentRowProps) {
  const { apartment, isRoom } = row;
  const family = familyLine(isRoom, roomCount);
  return (
    <li className={cn('flex items-stretch', isRoom && 'bg-card-alt/40')}>
      {roomCount > 0 && (
        <FamilyToggle open={open} onToggle={onToggle} name={apartmentLabel(apartment)} />
      )}
      <Link
        to="/properties/$id"
        params={{ id: apartment.id }}
        className={cn(
          'hover:bg-card-alt/50 focus-visible:ring-sage flex min-h-14 min-w-0 flex-1 items-center gap-3 px-4 py-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset',
          roomCount > 0 && 'pl-1',
          isRoom && 'pl-12',
        )}
      >
        <span className="min-w-0 flex-1">
          <span className="text-ink block truncate font-medium">{apartmentLabel(apartment)}</span>
          <span className="text-ink-soft block text-xs">
            {family ? `${family} · ` : ''}
            {capacity}
          </span>
        </span>
        <StatusPill status={apartment.status} />
        <ChevronRight size={16} className="text-ink-faint shrink-0" aria-hidden />
      </Link>
    </li>
  );
}

function FamilyToggle({
  open,
  onToggle,
  name,
}: {
  open: boolean;
  onToggle: () => void;
  name: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-label={`Zimmer von ${name} ${open ? 'einklappen' : 'ausklappen'}`}
      className="focus-visible:ring-sage text-ink-soft inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2"
    >
      <ChevronDown
        size={14}
        className={cn('transition-transform', !open && '-rotate-90')}
        aria-hidden
      />
    </button>
  );
}

/** Stellung im Wohnungsverbund als Nebenzeile unter dem Namen. */
function familyLine(isRoom: boolean, roomCount: number): string | null {
  if (isRoom) return 'Zimmer';
  return roomCount > 0 ? `Gesamtwohnung · ${roomCount} Zimmer` : null;
}

function StatusPill({ status }: { status: PropertyLifecycleStatus }) {
  return (
    <Pill variant={LIFECYCLE_VARIANT[status]} size="sm">
      {statusLabel(status)}
    </Pill>
  );
}
